/**
 * @tanstack/react-query hooks for the Expense Tracking module (Phase
 * 6.3). Thin wrappers over lib/api/expenses.ts so pages stay
 * declarative and caching/invalidation lives in one place.
 *
 * Query-key convention mirrors use-leave.ts / use-hr.ts:
 *   ["expenses", <area>, ...params]  — flush whole module with
 *                                      ["expenses"]
 *
 * The shared QueryClient (components/providers.tsx) sets retry: 1,
 * refetchOnWindowFocus: false, staleTime: 30s. The category catalogue
 * changes rarely → longer staleTime; expense/receipt/batch lists use
 * the default.
 *
 * There is NO GET /expenses/{id} endpoint (deliberate, like leave): a
 * single expense is DERIVED from the role-scoped list cache — see
 * useExpenseFromList. There is no expense-timeline table; the expense's
 * own lifecycle fields (created/decided/decision_notes/reimbursed_batch)
 * are the whole "timeline".
 *
 * ALL batch routes (incl. the GETs) are RBAC hr_manager/company_admin
 * server-side — a non-HR caller's batch query 403s, so those queries
 * use retry:false and the page degrades gracefully.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { expenseApi } from "@/lib/api";
import { invalidateApprovalsSummary } from "@/lib/approvals-invalidate";
import type {
  CreateBatchRequest,
  CreateExpenseRequest,
  UpdateExpenseRequest,
  Expense,
  ExpenseCategoryRequest,
  ExpenseDecisionRequest,
  MarkBatchPaidRequest,
} from "@/types/expenses";

const CATALOGUE_STALE = 5 * 60 * 1000; // 5 min — category catalogue

export const expenseKeys = {
  all: ["expenses"] as const,
  categories: () => [...expenseKeys.all, "categories"] as const,
  // `view` discriminates the server-side row scope: "excl-self" (approver
  // queue minus the caller's own claims), "mine" (only the caller's own
  // claims) or the default role scope.
  list: (
    status?: string,
    from?: string,
    to?: string,
    view?: "excl-self" | "mine",
  ) =>
    [
      ...expenseKeys.all,
      "list",
      status ?? "all",
      from ?? "any",
      to ?? "any",
      view ?? "role",
    ] as const,
  receipts: (expenseId: number) =>
    [...expenseKeys.all, "receipts", expenseId] as const,
  batches: (status?: string) =>
    [...expenseKeys.all, "batches", status ?? "all"] as const,
  batch: (id: number) => [...expenseKeys.all, "batch", id] as const,
};

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

export function useExpenseCategories() {
  return useQuery({
    queryKey: expenseKeys.categories(),
    queryFn: ({ signal }) => expenseApi.listExpenseCategories(signal),
    staleTime: CATALOGUE_STALE,
  });
}

function invalidateCategories(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: expenseKeys.categories() });
}

export function useCreateExpenseCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ExpenseCategoryRequest) =>
      expenseApi.createExpenseCategory(body),
    onSuccess: () => invalidateCategories(qc),
  });
}

export function useUpdateExpenseCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: ExpenseCategoryRequest }) =>
      expenseApi.updateExpenseCategory(vars.id, vars.body),
    onSuccess: () => invalidateCategories(qc),
  });
}

// ---------------------------------------------------------------------------
// Expenses
// ---------------------------------------------------------------------------

export function useExpenses(
  status?: string,
  from?: string,
  to?: string,
  opts?: { excludeSelf?: boolean; mine?: boolean },
) {
  const view = opts?.excludeSelf ? "excl-self" : opts?.mine ? "mine" : undefined;
  return useQuery({
    queryKey: expenseKeys.list(status, from, to, view),
    queryFn: ({ signal }) =>
      expenseApi.listExpenses(
        {
          ...(status ? { status } : {}),
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
          ...(opts?.excludeSelf ? { exclude_self: true } : {}),
          ...(opts?.mine ? { mine: true } : {}),
        },
        signal,
      ),
  });
}

/**
 * Derive a single expense from the (already role-scoped) UNFILTERED
 * list query.
 *
 * There is NO GET /expenses/{id} endpoint and no timeline table
 * (deliberate — same pattern as leave 4.3). The detail page loads the
 * list with NO status/date filter, so it includes every state the
 * caller's SQL scope allows, and finds the row by id. We key off the
 * "all" list query so a fresh load (or an invalidation after a
 * submit/decision/delete) refetches and the derived row stays
 * consistent.
 */
export function useExpenseFromList(id: number): {
  expense: Expense | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
} {
  const q = useExpenses();
  const expense = q.data?.expenses.find((e) => e.id === id);
  return {
    expense,
    isLoading: q.isLoading,
    isError: q.isError,
    error: q.error,
  };
}

/**
 * A create/submit/decision/delete changes the (status- and date-
 * filtered) expense lists AND any batch-eligible pool, so flush the
 * whole expenses root rather than guess which buckets moved (mirrors
 * the leave/HR list-flush rule).
 */
function invalidateExpensesAfterMutation(
  qc: ReturnType<typeof useQueryClient>,
) {
  void qc.invalidateQueries({ queryKey: expenseKeys.all });
  // The Inbox / tab badges count submitted claims (phantom-count fix).
  invalidateApprovalsSummary(qc);
}

export function useCreateExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateExpenseRequest) =>
      expenseApi.createExpense(body),
    onSuccess: () => invalidateExpensesAfterMutation(qc),
  });
}

export function useSubmitExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => expenseApi.submitExpense(id),
    onSuccess: () => invalidateExpensesAfterMutation(qc),
  });
}

export function useApproveExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body?: ExpenseDecisionRequest }) =>
      expenseApi.approveExpense(vars.id, vars.body),
    onSuccess: () => invalidateExpensesAfterMutation(qc),
  });
}

export function useRejectExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body?: ExpenseDecisionRequest }) =>
      expenseApi.rejectExpense(vars.id, vars.body),
    onSuccess: () => invalidateExpensesAfterMutation(qc),
  });
}

export function useSendBackExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; notes: string }) =>
      expenseApi.sendBackExpense(vars.id, { notes: vars.notes }),
    onSuccess: () => invalidateExpensesAfterMutation(qc),
  });
}

export function useUpdateExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: UpdateExpenseRequest }) =>
      expenseApi.updateExpense(vars.id, vars.body),
    onSuccess: () => invalidateExpensesAfterMutation(qc),
  });
}

export function useDeleteExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => expenseApi.deleteExpense(id),
    onSuccess: () => invalidateExpensesAfterMutation(qc),
  });
}

// ---------------------------------------------------------------------------
// Receipts
// ---------------------------------------------------------------------------

export function useExpenseReceipts(
  expenseId: number,
  enabled = true,
) {
  return useQuery({
    queryKey: expenseKeys.receipts(expenseId),
    queryFn: ({ signal }) =>
      expenseApi.listExpenseReceipts(expenseId, signal),
    enabled:
      enabled && Number.isFinite(expenseId) && expenseId > 0,
  });
}

/**
 * Adding/removing a receipt changes that expense's receipt list AND can
 * unblock the draft→submit gate (a requires_receipt category), so flush
 * the receipts query for this expense AND the expense lists.
 */
function invalidateReceipts(
  qc: ReturnType<typeof useQueryClient>,
  expenseId: number,
) {
  void qc.invalidateQueries({
    queryKey: expenseKeys.receipts(expenseId),
  });
  void qc.invalidateQueries({
    queryKey: [...expenseKeys.all, "list"],
  });
}

export function useUploadExpenseReceipt(expenseId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) =>
      expenseApi.uploadExpenseReceipt(expenseId, file),
    onSuccess: () => invalidateReceipts(qc, expenseId),
  });
}

export function useDeleteExpenseReceipt(expenseId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (receiptId: number) =>
      expenseApi.deleteExpenseReceipt(receiptId),
    onSuccess: () => invalidateReceipts(qc, expenseId),
  });
}

// ---------------------------------------------------------------------------
// Reimbursement batches (RBAC HR — queries 403 for a non-HR caller)
// ---------------------------------------------------------------------------

export function useBatches(status?: string, enabled = true) {
  return useQuery({
    queryKey: expenseKeys.batches(status),
    queryFn: ({ signal }) =>
      expenseApi.listBatches(status ? { status } : undefined, signal),
    // Every batch route is HR-only; don't spin retrying a hard authz
    // fail for a non-HR caller. The caller also gates `enabled` on the
    // expense-admin role so a non-HR direct-nav never fires the
    // guaranteed-403 round-trip (the page shows the HR-only state via
    // the role check, not a failed query — mirrors useEmployees).
    enabled,
    retry: false,
  });
}

export function useBatch(id: number, enabled = true) {
  return useQuery({
    queryKey: expenseKeys.batch(id),
    queryFn: ({ signal }) => expenseApi.getBatch(id, signal),
    enabled: enabled && Number.isFinite(id) && id > 0,
    retry: false,
  });
}

/**
 * A batch create/transition moves expenses between the batch-eligible
 * pool and reimbursed AND changes every batch status bucket + the
 * single-batch view, so flush the whole expenses root.
 */
function invalidateBatchesAfterMutation(
  qc: ReturnType<typeof useQueryClient>,
) {
  void qc.invalidateQueries({ queryKey: expenseKeys.all });
}

export function useCreateBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBatchRequest) =>
      expenseApi.createBatch(body),
    onSuccess: () => invalidateBatchesAfterMutation(qc),
  });
}

export function useMarkBatchProcessing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => expenseApi.markBatchProcessing(id),
    onSuccess: () => invalidateBatchesAfterMutation(qc),
  });
}

export function useMarkBatchPaid() {
  const qc = useQueryClient();
  return useMutation({
    // Wave 2: optional payment account the batch was paid from.
    mutationFn: ({ id, body }: { id: number; body?: MarkBatchPaidRequest }) =>
      expenseApi.markBatchPaid(id, body),
    onSuccess: () => invalidateBatchesAfterMutation(qc),
  });
}

export function useCancelBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => expenseApi.cancelBatch(id),
    onSuccess: () => invalidateBatchesAfterMutation(qc),
  });
}
